import { StudioContext, useStudioProject } from '@/components/studio/StudioContext'
import { StudioBoardCanvas } from '@/components/studio/StudioBoardCanvas'
import { StudioModeControl } from '@/components/studio/StudioModeControl'
import type { ZoomApi } from '@genieartinc/art-canvas'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import type { StudioFrame } from '@genieartinc/schemas/graph'

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { motion, MotionConfig } from 'motion/react'
import { Icon } from '@/components/ui/icon'
import { type ReactFlowInstance, type Node, type Edge } from '@xyflow/react'
import { WorkspacePanel, WorkspacePanelControls, WorkspaceDock, WorkspaceDockPanel, useToolSidebar } from '@genieartinc/studio-ui'
import { StudioSessionTabs } from '@/components/studio/StudioSessionTabs'
import { FilesPanel } from '@/components/canvas/files-panel/files-panel'
import { CanvasToolbar } from '@/components/canvas/toolbar'
import { LibraryPanel } from '@/components/canvas/library/LibraryPanel'
import { ShaderCodeModal } from '@/components/canvas/shader/ShaderCodeModal'
import { ShaderGenerationScope } from '@/components/canvas/shader/ShaderGenerationScope'
import { subscribeShaderLibraryClose } from '@/components/canvas/shader/library-focus'
import { CreateModelDialog } from '@/components/workspace/CreateModelDialog'
import { NodeSettingsPanel } from '@/components/canvas/editor/node-settings'
import { CanvasDropZone } from '@/components/canvas/editor/CanvasDropZone'
import { CustomDragPreview } from '@/components/canvas/nodes/components/ui'
import { CANVAS_DROP_ZONE_CONFIG, CanvasViewportContext } from '@/constants/canvas'
import { toReactFlowNode } from '@/lib/canvas/to-react-flow-node'
import { createCommentNode, createTextNode, createImageNode, createVideoNode, createNode3D, createTextFileNode, createImageFileNode } from '@/lib/canvas'
import { FEATURES } from '@/lib/features'
import { createShaderNode } from '@/lib/canvas/utils/nodeFactory'
import type { LibrarySketch } from '@/lib/canvas/shader/library'
import type { CatalogModel } from '@/lib/api/models'
import { DROPDOWN_ITEMS_MAP } from '@/components/canvas/toolbar/toolbar-config'

// Page-specific imports
import { useCanvasPage } from '@/hooks/canvas/useCanvasPage'
import { useCanvasActions } from '@/hooks/canvas/useCanvasActions'
import { useCanvasSave } from '@/hooks/canvas/useCanvasSave'
import { useConnectionDropMenu } from '@/hooks/canvas/useConnectionDropMenu'
import { useCanvasWindowGlobals } from '@/hooks/canvas/useCanvasWindowGlobals'
import { useCanvasKeyboard } from '@/hooks/canvas/useCanvasKeyboard'
import { useCanvasNodeSettings } from '@/hooks/canvas/useCanvasNodeSettings'
import { useCanvasSidebarConfig } from '@/hooks/canvas/useCanvasSidebarConfig'
import { useCanvasNodeHandlers } from '@/hooks/canvas/useCanvasNodeHandlers'
import { useCanvasToolSelect } from '@/hooks/canvas/useCanvasToolSelect'
import { useCanvasFiles } from '@/hooks/canvas/useCanvasFiles'
import { useCanvasWebSocket } from '@/hooks/canvas/useCanvasWebSocket'
import { useCanvasActivity } from '@/hooks/canvas/useCanvasActivity'
import { useWalletHistory } from '@/hooks/useWalletHistory'
import { useJobActions } from '@/hooks/canvas/useJobActions'
import { useJobStore } from '@/stores/jobStore'
import { CanvasModals } from '@/components/canvas/editor/CanvasModals'
import { FullScreenLoader } from '@/components/ui/FullScreenLoader'
// import { RenderCounter } from '@/components/canvas/editor/RenderCounter'
import { ConnectionDropMenu } from '@/components/canvas/editor/ConnectionDropMenu'
import type { CanvasTool, CanvasMode, NodeSettingsPanelRef } from '@/types/canvas'
import { useShortcuts } from '@/components/canvas/hooks/useShortcuts'
import { useUndoRedo } from '@/components/canvas/hooks/useUndoRedo'
import { useImmerUndoRedo } from '@/components/canvas/hooks/useImmerUndoRedo'
import { DarkModeProvider } from '@/components/canvas/providers/DarkModeProvider'
import { staggerContainer, staggerItem } from '@/lib/animations'
import { ActivityPanel } from '@/components/canvas/activity-panel/activity-panel'
// BatchRunnerPanel kept in codebase — will be reused by Batch Generator node
// import { BatchRunnerPanel } from '@/components/canvas/batch-runner/batch-runner-panel'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'
import { useContainerNavigationStore } from '@/stores/useContainerNavigationStore'
import { CanvasCreditsPanel } from '@/components/canvas/editor/sidebar/CanvasSidebarCredits'
import { KeyboardShortcutsOverlay } from '@/components/canvas/KeyboardShortcutsOverlay'
import { WorkspaceCommandDialog } from '@/components/canvas/WorkspaceCommandDialog'
import { useWorkspaceKeys } from '@/lib/canvas/useWorkspaceKeys'
import { WEB_HOST, applyWorkspaceCommand, detectPlatform, shortcutLabel, type ChromeState, type PanelId } from '@/lib/canvas/workspace-commands'
import { CommandPalette } from '@/components/canvas/CommandPalette'
import type { CommandContext } from '@/lib/canvas/command-registry'
import { useCanvasContextMenu } from '@/hooks/canvas/useCanvasContextMenu'
import { CanvasContextMenu } from '@/components/canvas/menus/CanvasContextMenu'
import { useNodeAlignment } from '@/hooks/canvas/useNodeAlignment'
import { MultiSelectionBar } from '@/components/canvas/MultiSelectionBar'
import { StatusBar } from '@/components/canvas/StatusBar'
import type { GenieCanvasNode } from '@/lib/canvas/types'
import { InsufficientBalanceDialog } from '@/components/canvas/nodes/base/InsufficientBalanceDialog'
// Save status is shown in the active session tab.

// Performance monitor — exposes window.__canvasPerf in dev
import '@/components/canvas/utils/performance-monitor'
import { resolveCollisions } from '@/components/canvas/utils/resolve-collisions'
import { usePostHog } from 'posthog-js/react'

export function CanvasPage() {
  // Canvas state from hook
  const {
    currentCanvas,
    canvasId,
    isLoading,
    authLoading,
    mounted,
    pages,
    activePage,
    setActivePage,
    isPagesLoading,
    refetchPages,
    loadedPage,
    viewport,
    setViewport,
    markViewportInteracted,
    nodes,
    setNodes: rawSetNodes,
    edges,
    setEdges: rawSetEdges,
    theme,
    selectedNodes,
    setSelectedNodes,
    updateLocalCanvas,
  } = useCanvasPage()

  const studioFlag = useFeatureFlag('one-genie-two-modes')
  const studioEnabled = import.meta.env.DEV || studioFlag
  const showStudioNode = useCallback((graphId: string, nodeId: string) => {
    const page = pages.find(p => p.uuid === graphId || p.id === graphId)
    if (page) setActivePage(page.id)
    setSelectedNodes([nodeId])
    window.setTimeout(() => { void canvasRef.current?.fitView({ nodes: [{ id: nodeId }], padding: 0.5 }) }, 200)
  }, [pages, setActivePage, setSelectedNodes])
  const studio = useStudioProject(studioEnabled ? canvasId : null, showStudioNode)
  const isBoardMode = studioEnabled && studio.mode === 'board'
  const boardZoomApi = useRef<ZoomApi | null>(null)
  const [boardZoom, setBoardZoom] = useState(1)
  const onBoardZoomApi = useCallback((api: ZoomApi | null) => { boardZoomApi.current = api }, [])

  // Undo/redo system (Immer-based: setNodes/setEdges automatically track history)
  const { setNodes, setEdges, undo, redo } = useImmerUndoRedo({
    nodes, edges, rawSetNodes, rawSetEdges,
  })

  // Canvas file/folder system
  const {
    folders,
    loading: foldersLoading,
    createFolder,
    renameFolder,
    deleteFolder,
    duplicateFolder,
    files,
    filesLoading,
    moveFile,
    uploadFile,
    deleteFile,
  } = useCanvasFiles({ canvasId })

  // Canvas ref (declared early for job hooks)
  const canvasRef = useRef<ReactFlowInstance>(null)

  // Job system: WebSocket + activity panel
  const loadCanvasJobs = useJobStore((s) => s.loadCanvasJobs)
  const setJobCanvasId = useJobStore((s) => s.setCanvasId)

  // Load active jobs on canvas mount
  useEffect(() => {
    if (canvasId) {
      setJobCanvasId(canvasId)
      loadCanvasJobs(canvasId)
    }
  }, [canvasId, setJobCanvasId, loadCanvasJobs])

  // WebSocket connection for real-time job updates
  useCanvasWebSocket(canvasId || null)

  // Listen for node removal events from WebSocket error handler
  useEffect(() => {
    const handler = (e: Event) => {
      const { nodeIds } = (e as CustomEvent<{ nodeIds: string[] }>).detail
      const idSet = new Set(nodeIds)
      setNodes(prev => prev.filter(n => !idSet.has(n.id)))
      setEdges(prev => prev.filter(e => !idSet.has(e.source) && !idSet.has(e.target)))
      // Clean up external series data store
      for (const nodeId of nodeIds) {
        useSeriesDataStore.getState().clearNode(nodeId)
      }
    }
    window.addEventListener('genie:remove-nodes', handler)
    return () => window.removeEventListener('genie:remove-nodes', handler)
  }, [setNodes, setEdges])

  // Job actions (create, cancel, retry, goToNode)
  const { cancelJob, retryJob, goToNode } = useJobActions({ canvasRef })

  // Activity panel data (maps jobs to ActivityItem format)
  const {
    activities,
    hasActiveJobs,
    handleGoToNode,
    handleActivityClick,
  } = useCanvasActivity({ canvasRef, onCancelJob: cancelJob, onRetryJob: retryJob })

  const {
    transactions: historyTransactions,
    isLoading: historyLoading,
    error: historyError,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
  } = useWalletHistory(canvasId)

  // UI state

  const [activeTool, setActiveTool] = useState<CanvasTool>(undefined)
  const [activeMode, setActiveMode] = useState<CanvasMode>('creator')
  const [canvasSize, setCanvasSize] = useState(() => {
    if (typeof window !== 'undefined') {
      return { width: window.innerWidth, height: window.innerHeight }
    }
    return { width: 1920, height: 1080 }
  })

  // Shortcuts overlay + command palette + library
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showCommandPalette, setShowCommandPalette] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [libraryTab, setLibraryTab] = useState<string>()
  const [libraryHost, setLibraryHost] = useState<HTMLDivElement | null>(null)
  const [shaderCodeModalNodeId, setShaderCodeModalNodeId] = useState<string | null>(null)

  // View toggles (Controls & MiniMap)
  const [showControls, setShowControls] = useState(false)
  const [showMiniMap, setShowMiniMap] = useState(false)
  const [trainDialogOpen, setTrainDialogOpen] = useState(false)

  // Track mouse position for cursor-based node placement (keyboard shortcuts)
  const mousePositionRef = useRef({ x: 0, y: 0 })

  // Track mouse position for cursor-based node placement
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY }
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // Prevent browser back/forward swipe gesture on canvas
  useEffect(() => {
    // Lock overscroll on html+body to block browser navigation gesture
    document.documentElement.style.overscrollBehaviorX = 'none'
    document.body.style.overscrollBehaviorX = 'none'

    // Intercept horizontal wheel/trackpad at document level
    const preventSwipe = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > 0) {
        e.preventDefault()
      }
    }
    document.addEventListener('wheel', preventSwipe, { passive: false })

    return () => {
      document.removeEventListener('wheel', preventSwipe)
      document.documentElement.style.overscrollBehaviorX = ''
      document.body.style.overscrollBehaviorX = ''
    }
  }, [])

  // Track canvas size changes
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.querySelector('.canvas-page')
      if (container) {
        const rect = container.getBoundingClientRect()
        setCanvasSize({ width: rect.width, height: rect.height })
      }
    }

    updateCanvasSize()
    window.addEventListener('resize', updateCanvasSize)
    return () => window.removeEventListener('resize', updateCanvasSize)
  }, [])

  // Canvas actions
  const {
    canvasActions,
    handleRenameConfirm,
    handleDeleteConfirm,
    handleDuplicateConfirm,
    handleNewCanvasConfirm,
    showRenameDialog,
    setShowRenameDialog,
    showDeleteDialog,
    setShowDeleteDialog,
    showDuplicateDialog,
    setShowDuplicateDialog,
    showNewCanvasDialog,
    setShowNewCanvasDialog,
    newCanvasName,
    setNewCanvasName,
  } = useCanvasActions(currentCanvas, canvasRef, viewport, {
    showControls,
    onToggleControls: useCallback(() => setShowControls(prev => !prev), []),
    showMiniMap,
    onToggleMiniMap: useCallback(() => setShowMiniMap(prev => !prev), []),
  })

  // Applied when the save system reconciles this tab's content with the server
  // (another tab saved first, or work was replayed after a close). It is a real
  // change to the canvas, so it goes through the history-tracked setters.
  const handleContentReconciled = useCallback((nextNodes: Node[], nextEdges: Edge[]) => {
    setNodes(() => nextNodes)
    setEdges(() => nextEdges)
  }, [setNodes, setEdges])

  // Save system (Figma-style auto-save)
  const {
    saveState,
    saveTitle,
    saveNodes,
    saveEdges,
    saveViewport,
    saveConfig,
  } = useCanvasSave(canvasId, currentCanvas, activePage, pages, {
    nodes,
    edges,
    loadedPage,
    onContentReconciled: handleContentReconciled,
  })

  // Sidebar config persistence
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
  } = useCanvasSidebarConfig({
    currentCanvas,
    activePage,
    setActivePage,
    pages,
    saveConfig,
  })

  // Stable callbacks for connection drop menu
  const handleConnectionEdgesChange = useCallback((newEdges: typeof edges) => {
    setEdges(newEdges)
    saveEdges(newEdges)
  }, [setEdges, saveEdges])

  const handleConnectionNodesChange = useCallback((newNodes: Node[] | GenieCanvasNode[]) => {
    setNodes(newNodes as Node[])
    saveNodes(newNodes as Node[])
  }, [setNodes, saveNodes])

  // Connection drop menu
  const connectionDropMenu = useConnectionDropMenu({
    viewport,
    canvasSize,
    edges,
    nodes: nodes as unknown as GenieCanvasNode[],
    canvasId,
    onEdgesChange: handleConnectionEdgesChange,
    onNodesChange: handleConnectionNodesChange,
  })

  // Window global callbacks (handle updates, placeholder creation, node state patches)
  useCanvasWindowGlobals({
    canvasId,
    nodes,
    setNodes,
    setEdges,
    saveNodes,
    saveEdges,
    toReactFlowNode,
  })

  // Keyboard interactions (space key, zoom prevention, shortcuts)
  const {
    isSpacePressed,
    isPanning,
    setIsPanning,
  } = useCanvasKeyboard({
    enabled: !isBoardMode,
    selectedNodes,
    canvasActions,
  })

  // Node settings panel state and selection logic
  const {
    nodeSettingsVisible,
    nodeSettingsCollapsed,
    setNodeSettingsCollapsed,
    selectedNodeData,
    handleNodeSettingsCollapse,
  } = useCanvasNodeSettings({
    nodes,
    edges,
    selectedNodes,
  })

  const [toolPaletteHost, setToolPaletteHost] = useState<HTMLDivElement | null>(null)
  const [paletteWidth, setPaletteWidth] = useState(48)
  const toolSidebar = useToolSidebar()
  const [dockSides, setDockSides] = useState({ left: false, right: false, leftWidth: 0, rightWidth: 0 })
  const [filesExpanded, setFilesExpanded] = useState(true)
  const [activityPanelOpen, setActivityPanelOpen] = useState(true)
  const [nodeControlsOpen, setNodeControlsOpen] = useState(false)
  const [chromeHidden, setChromeHidden] = useState(false)
  const [workspaceSearchOpen, setWorkspaceSearchOpen] = useState(false)
  useEffect(() => { if (!isBoardMode && selectedNodeData?.id) setNodeControlsOpen(true) }, [isBoardMode, selectedNodeData?.id])

  // Node and edge handlers
  const {
    handleSelectionChange,
    handlePaneClick,
    handleNodesChange,
    handleEdgesChange,
    handleConnect,
    handleNodeStateChange,
  } = useCanvasNodeHandlers({
    canvasRef,
    setNodes,
    setEdges,
    setSelectedNodes,
    saveNodes,
    saveEdges,
  })


  // Keyboard shortcut: toggle properties panel (open AND close)
  const handleToggleProperties = useCallback(() => {
    if (nodeSettingsVisible) {
      handleNodeSettingsCollapse()
    }
    // If no node is selected, P key does nothing (nothing to toggle)
  }, [nodeSettingsVisible, handleNodeSettingsCollapse])

  // Keyboard shortcut: toggle sidebar
  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev)
  }, [setSidebarCollapsed])

  const handleToggleAllSidebars = useCallback(() => {
    const current: ChromeState = {
      mode: isBoardMode ? 'board' : 'flow',
      visibility: {
        sidebar: toolSidebar.open,
        files: !sidebarCollapsed,
        library: libraryOpen,
        activity: activityPanelOpen,
        tools: nodeControlsOpen,
        genie: false,
        apps: false,
      },
      layout: 'genie.workspace.panel-layout',
      selection: selectedNodes,
      chromeHidden,
      editorOpen: false,
    }
    const command = { id: 'chrome.toggle-ui', label: 'Hide all toolbars and panels', group: 'View', keywords: [], kind: 'chrome' as const, shortcutMac: '', shortcutOther: '', available: true }
    const next = applyWorkspaceCommand(current, command)
    setChromeHidden(next.state.chromeHidden)
  }, [isBoardMode, toolSidebar.open, sidebarCollapsed, libraryOpen, activityPanelOpen, nodeControlsOpen, selectedNodes, chromeHidden])

  const platform = detectPlatform()
  const workspaceMode = isBoardMode ? 'board' as const : 'flow' as const
  const applyPanel = (panel: PanelId, open: boolean) => {
    if (panel === 'sidebar') toolSidebar.setOpen(open)
    if (panel === 'files') setSidebarCollapsed(!open)
    if (panel === 'library') setLibraryOpen(open)
    if (panel === 'activity') setActivityPanelOpen(open)
    if (panel === 'tools') { setNodeControlsOpen(open); if (open) setNodeSettingsCollapsed(false) }
  }
  useWorkspaceKeys(() => ({
    host: WEB_HOST,
    mode: workspaceMode,
    chromeHidden,
    editorOpen: false,
    selectionCount: isBoardMode ? Number(Boolean(studio.selectedFrameId)) : selectedNodes.length,
  }), (command) => {
    const current: ChromeState = {
      mode: workspaceMode,
      visibility: {
        sidebar: toolSidebar.open,
        files: !sidebarCollapsed,
        library: libraryOpen,
        activity: activityPanelOpen,
        tools: nodeControlsOpen,
        genie: false,
        apps: false,
      },
      layout: 'genie.workspace.panel-layout',
      selection: selectedNodes,
      chromeHidden,
      editorOpen: false,
    }
    const next = applyWorkspaceCommand(current, command)
    if (next.effect.type === 'chrome') setChromeHidden(next.state.chromeHidden)
    if (next.effect.type === 'mode' && next.effect.mode !== 'chat') studio.setMode(next.effect.mode)
    if (next.effect.type === 'panel') applyPanel(next.effect.panel, next.effect.open)
    if (next.effect.type === 'help') setShowShortcuts(true)
    if (next.effect.type === 'search' && next.effect.which === 'actions') setWorkspaceSearchOpen(true)
    if (next.effect.type === 'search' && next.effect.which === 'tools') { setLibraryOpen(true); toolSidebar.setOpen(true) }
    if (next.effect.type === 'zoom' && isBoardMode) {
      const api = boardZoomApi.current
      if (next.effect.action === 'in') api?.zoomIn()
      else if (next.effect.action === 'out') api?.zoomOut()
      else if (next.effect.action === 'fit') api?.fit()
      else if (next.effect.action === 'selection') api?.fitSelection?.()
      else api?.reset()
    }
  })

  // Handle tool selection - add nodes (extracted hook)
  const { handleToolSelect, handleToolDrop } = useCanvasToolSelect({
    canvasRef,
    canvasId,
    setNodes,
    setActiveTool,
    onOpenTrainDialog: () => setTrainDialogOpen(true),
    onOpenLibrary: () => setLibraryOpen(true),
  })

  // Cancel placement mode on Escape
  useEffect(() => {
    if (!activeTool) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveTool(undefined)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [activeTool, setActiveTool])

  // Comment placement: click-to-place when activeTool === 'comment'
  const handleCanvasPaneClick = useCallback((event: React.MouseEvent) => {
    if (activeTool === 'comment' && canvasRef.current) {
      const position = canvasRef.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })
      const node = createCommentNode(position)
      if (node) {
        if (canvasId && node.component?.state) {
          node.component.state.canvasId = canvasId
        }
        setNodes((prev) => [...prev, toReactFlowNode(node)])
      }
      setActiveTool(undefined)
      return
    }
    handlePaneClick()
  }, [activeTool, canvasRef, canvasId, setNodes, setActiveTool, handlePaneClick])

  // Draw-to-create section tool
  // Keyboard shortcut: quick-add node at cursor position
  const handleQuickAddNode = useCallback((type: 'text' | 'image' | 'video' | 'sound' | 'shader') => {
    const toolMap: Record<string, string> = {
      text: 'text',
      image: 'image-create',
      video: 'video-create',
      sound: 'sound',
      shader: 'shader',
    }
    handleToolSelect(toolMap[type] || type, mousePositionRef.current)
  }, [handleToolSelect])

  // Variant switch: number keys 1-5 change selected node's variant in-place
  const handleVariantSwitch = useCallback((variantIndex: number) => {
    setNodes((prev) => {
      const selected = prev.filter((n) => n.selected)
      if (selected.length !== 1) return prev

      const node = selected[0]
      const nodeType = node.type || ''

      // Map node component type → toolbar category → dropdown items
      const typeToCategory: Record<string, string> = {
        TextNode: 'text', TextFile: 'text',
        ImageNode: 'image', ImageFile: 'image',
        VideoNode: 'video',
        ThreeDNode: '3d',
        // Kept so an existing canvas that still holds a 3D node maps to its category.
        Node3D: '3d',
      }

      const category = typeToCategory[nodeType]
      if (!category) return prev

      const items = DROPDOWN_ITEMS_MAP[category]
      if (!items || variantIndex < 1 || variantIndex > items.length) return prev

      const targetToolId = items[variantIndex - 1].id

      const pos = node.position
      const toolCreators: Record<string, () => unknown> = {
        'text': () => createTextNode(pos),
        'text-file': () => createTextFileNode(pos),
        'image-create': () => createImageNode(pos, 'create'),
        'image-edit': () => createImageNode(pos, 'edit'),
        'image-enhance': () => createImageNode(pos, 'enhance'),
        'image-file': () => createImageFileNode(pos),
      }

      const creator = toolCreators[targetToolId]
      if (!creator) return prev

      const genieNode = creator() as import('@/lib/canvas/types').GenieCanvasNode | null
      if (!genieNode) return prev

      if (canvasId && genieNode.component?.state) {
        genieNode.component.state.canvasId = canvasId
      }

      const rfNode = toReactFlowNode(genieNode) as Node
      rfNode.selected = true

      // Replace old node with new variant in single atomic update
      return prev.map((n) => n.id === node.id ? rfNode : n)
    })
  }, [setNodes, canvasId])

  // Keyboard shortcuts (canvas + UI)
  const shortcutActions = useShortcuts(
    { setNodes, setEdges, canvasRef, enabled: !isBoardMode },
    {
      onUndo: undo,
      onRedo: redo,
      onToggleShortcuts: () => setShowShortcuts((prev) => !prev),
      onToggleProperties: handleToggleProperties,
      onToggleSidebar: handleToggleSidebar,
      onToggleAllSidebars: handleToggleAllSidebars,
      onAddNode: handleQuickAddNode,
      onVariantSwitch: handleVariantSwitch,
      onOpenCommandPalette: () => setShowCommandPalette(true),
      onOpenLibrary: () => setLibraryOpen(prev => !prev),
    },
  )

  // Command palette context (memoized to avoid unnecessary re-renders)
  const commandContext = useMemo<CommandContext>(() => ({
    selectedNodes: selectedNodes.map(id => nodes.find(n => n.id === id)).filter(Boolean) as import('@xyflow/react').Node[],
    selectedEdges: [],
    nodes,
    edges,
    canvasRef,
    setNodes,
    setEdges,
    actions: {
      undo,
      redo,
      deleteSelected: shortcutActions.deleteSelected,
      selectAll: shortcutActions.selectAll,
      deselectAll: shortcutActions.deselectAll,
      duplicateSelected: shortcutActions.duplicateSelected,
      copySelected: shortcutActions.copySelected,
      paste: shortcutActions.paste,
      cut: shortcutActions.cut,
      toggleLock: shortcutActions.toggleLock,
      centerOnSelected: shortcutActions.centerOnSelected,
      changeZIndex: shortcutActions.changeZIndex,
    },
    callbacks: {
      onToggleShortcuts: () => setShowShortcuts(prev => !prev),
      onToggleProperties: handleToggleProperties,
      onToggleSidebar: handleToggleSidebar,
      onToggleAllSidebars: handleToggleAllSidebars,
      onAddNode: handleQuickAddNode,
      onToolSelect: handleToolSelect,
    },
  }), [selectedNodes, nodes, edges, canvasRef, setNodes, setEdges, undo, redo, shortcutActions, handleToggleProperties, handleToggleSidebar, handleToggleAllSidebars, handleQuickAddNode, handleToolSelect])

  // Node alignment (multi-selection)
  const { alignNodes, distributeNodes } = useNodeAlignment(setNodes)

  // Context menu (right-click)
  const contextMenu = useCanvasContextMenu()

  const contextMenuActions = useMemo(() => ({
    paste: shortcutActions.paste,
    copySelected: shortcutActions.copySelected,
    cut: shortcutActions.cut,
    duplicateSelected: shortcutActions.duplicateSelected,
    deleteSelected: shortcutActions.deleteSelected,
    selectAll: shortcutActions.selectAll,
    toggleLock: shortcutActions.toggleLock,
    changeZIndex: shortcutActions.changeZIndex,
    addNode: (type: 'text' | 'image' | 'video') => handleQuickAddNode(type),
    fitView: () => canvasRef.current?.fitView({ duration: 300 }),
    deleteEdge: (edgeId: string) => setEdges(prev => prev.filter(e => e.id !== edgeId)),
    reverseEdge: (edgeId: string) => setEdges(prev => prev.map(e =>
      e.id === edgeId ? { ...e, source: e.target, target: e.source, sourceHandle: e.targetHandle, targetHandle: e.sourceHandle } : e
    )),
  }), [shortcutActions, handleQuickAddNode, canvasRef, setEdges])

  // Handle rename confirmation with local state update + save
  const handleRename = useCallback(async () => {
    const updated = await handleRenameConfirm()
    if (updated) {
      updateLocalCanvas(updated)
    }
  }, [handleRenameConfirm, updateLocalCanvas])

  // Handle viewport change with auto-save
  const handleViewportChange = useCallback((newViewport: typeof viewport) => {
    markViewportInteracted()
    setViewport(newViewport)
    saveViewport(newViewport)
  }, [markViewportInteracted, setViewport, saveViewport])

  // Auto-collapse settings panel when no node is selected

  // Ref for NodeSettingsPanel imperative API (passive mode)
  const nodeSettingsPanelRef = useRef<NodeSettingsPanelRef>(null)

  // Extract selected node's handles for the settings panel (avoids needing RF context)
  const selectedNodeHandles = useMemo(() => {
    if (!selectedNodeData?.id) return undefined
    const node = nodes.find(n => n.id === selectedNodeData.id)
    if (!node) return undefined
    const data = node.data as Record<string, unknown> | undefined
    return (data?.handles as import('@/lib/canvas/types').HandleMeta[]) || undefined
  }, [selectedNodeData?.id, nodes])

  // Selected node raw state (needed by shader settings which can't use RF hooks at page level)
  const selectedNodeRawState = useMemo(() => {
    if (!selectedNodeData?.id) return undefined
    const node = nodes.find(n => n.id === selectedNodeData.id)
    const data = node?.data as Record<string, unknown> | undefined
    return (data?.state as Record<string, unknown>) || undefined
  }, [selectedNodeData?.id, nodes])

  // Stable onStateChange for the selected node — avoids creating new function in render
  const selectedNodeStateChange = useMemo(
    () => selectedNodeData ? handleNodeStateChange(selectedNodeData.id) : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedNodeData?.id, handleNodeStateChange],
  )

  // Memoized panning handlers to avoid inline function re-creation
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isSpacePressed && e.button === 0) {
      setIsPanning(true)
    }
  }, [isSpacePressed, setIsPanning])

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false)
    }
  }, [isPanning, setIsPanning])

  // Collision resolution after node drag
  const handleNodeDragStop = useCallback((_event: React.MouseEvent, _node: Node, dragged: Node[]) => {
    const fixedIds = new Set(dragged.map(n => n.id))
    setNodes(prev => {
      const resolved = resolveCollisions(prev, { fixedIds })
      // Only save if positions actually changed
      const changed = resolved.some((n, i) =>
        n.position.x !== prev[i].position.x || n.position.y !== prev[i].position.y
      )
      if (changed) {
        saveNodes(resolved)
      }
      return resolved
    })
  }, [setNodes, saveNodes])

  const posthog = usePostHog()

  const handleLibraryOpen = useCallback(() => {
    posthog.capture('canvas_library_opened', { canvas_id: canvasId })
    setLibraryOpen((v) => !v)
  }, [posthog, canvasId])

  // Listen for library open events from node settings empty state
  useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent<{ tab?: string } | null>).detail?.tab
      setLibraryTab(typeof tab === 'string' ? tab : undefined)
      setLibraryOpen(true)
    }
    document.addEventListener('genie:open-library', handler)
    const closeLibrary = subscribeShaderLibraryClose(() => setLibraryOpen(false))
    return () => {
      document.removeEventListener('genie:open-library', handler)
      closeLibrary()
    }
  }, [])

  // Listen for shader code-editor open requests from the shader settings panel
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ nodeId?: string }>).detail
      if (detail?.nodeId) setShaderCodeModalNodeId(detail.nodeId)
    }
    document.addEventListener('genie:shader-open-editor', handler)
    return () => document.removeEventListener('genie:shader-open-editor', handler)
  }, [])

  const handleAddModelToCanvas = useCallback((model: CatalogModel) => {
    const pos = canvasRef.current
      ? canvasRef.current.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
      : { x: 0, y: 0 }

    const purpose = (model.purpose as 'create' | 'edit' | 'enhance' | 'tools') || 'create'
    const creators: Record<string, () => ReturnType<typeof createImageNode>> = {
      'image': () => createImageNode(pos, purpose),
      'video': () => createVideoNode(pos, purpose),
      // The Library filters 3D models out while the flag is off, so this is only reachable
      // if the flag is on; guarded anyway so a stale client cannot mint an unregistered node.
      ...(FEATURES.NODE_3D ? { '3d': () => createNode3D(pos, purpose) } : {}),
      'text-llm': () => createTextNode(pos),
    }

    const creator = creators[model.nodeType || 'image'] ?? (() => createImageNode(pos, purpose))
    const node = creator()
    if (node) {
      if (node.component?.state) {
        node.component.state.modelId = model.id
        node.component.state.canvasId = canvasId
      }
      setNodes((prev) => [...prev, toReactFlowNode(node)])
      posthog.capture('canvas_model_added_from_library', {
        canvas_id: canvasId,
        model_id: model.endpointId,
        node_type: model.nodeType,
      })
    }
  }, [canvasRef, canvasId, setNodes, posthog])

  const handleAddShaderToCanvas = useCallback((sketch: LibrarySketch) => {
    const pos = canvasRef.current
      ? canvasRef.current.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
      : { x: 0, y: 0 }

    const node = createShaderNode(pos)
    if (!node) return
    if (node.component?.state) {
      const state = node.component.state as Record<string, unknown>
      state.canvasId = canvasId
      state.title = sketch.name
      state.code = sketch.code
      state.runtimeMode = sketch.runtimeMode
      state.sketchId = sketch.id
      state.extractedParams = sketch.params
      state.paramValues = Object.fromEntries(sketch.params.map((p) => [p.name, p.default]))
    }
    setNodes((prev) => [...prev, toReactFlowNode(node)])
    posthog.capture('canvas_shader_added_from_library', {
      canvas_id: canvasId,
      sketch_id: sketch.id,
      runtime_mode: sketch.runtimeMode,
    })
  }, [canvasRef, canvasId, setNodes, posthog])

  const handleFileUpload = useCallback(async (file: File) => {
    const result = await uploadFile(file)
    posthog.capture('canvas_file_uploaded', {
      canvas_id: canvasId,
      file_type: file.type,
      file_size: file.size,
    })
    return result
  }, [uploadFile, posthog, canvasId])

  // ── Container navigation ──────────────────────────────────────────────
  const currentContainerId = useContainerNavigationStore((s) => s.currentContainerId)
  const containerStack = useContainerNavigationStore((s) => s.stack)
  const goBack = useContainerNavigationStore((s) => s.goBack)
  const goToRoot = useContainerNavigationStore((s) => s.goToRoot)

  // When entering/leaving a container, fit view to show all visible nodes
  useEffect(() => {
    // Small delay to let visibleNodes update before fitting
    const timer = setTimeout(() => {
      canvasRef.current?.fitView({ padding: 0.2, duration: 300 })
    }, 100)
    return () => clearTimeout(timer)
  }, [currentContainerId])

  // Filter nodes: show only nodes belonging to the current container level
  const visibleNodes = useMemo(() => {
    if (currentContainerId) {
      return nodes.filter((n) => {
        if (!n.data) return false
        const nState = (n.data as Record<string, unknown>)?.state as Record<string, unknown> | undefined
        return nState?.containerId === currentContainerId
      })
    }
    return nodes.filter((n) => {
      if (!n.data) return true
      const nState = (n.data as Record<string, unknown>)?.state as Record<string, unknown> | undefined
      return !nState?.containerId
    })
  }, [nodes, currentContainerId])

  // Get container name for breadcrumb
  const currentContainerName = useMemo(() => {
    if (!currentContainerId) return null
    const containerNode = nodes.find((n) => n.id === currentContainerId)
    const containerState = (containerNode?.data as Record<string, unknown>)?.state as Record<string, unknown> | undefined
    return (containerState?.seriesName as string) || (containerState?.title as string) || 'Gallery Container'
  }, [currentContainerId, nodes])

  const addFrameConnection = useCallback((type: 'FrameInput' | 'FrameOutput', frame?: StudioFrame) => {
    const id = crypto.randomUUID()
    const position = canvasRef.current?.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 }) ?? { x: 100, y: 100 }
    const graphId = pages.find(p => p.id === activePage)?.uuid ?? activePage ?? 'workflow'
    setNodes(prev => [...prev, { id, type, position, data: { handles: STANDARD_HANDLES[type], state: { graphId, ...(frame ? { frameId: frame.id, versionId: frame.versions[frame.activeVersion]?.id } : {}) } } }])
    studio.setMode('flow')
    setSelectedNodes([id])
  }, [activePage, pages, setNodes, setSelectedNodes, studio.setMode])

  // Show loading until mounted (prevents hydration mismatch)
  if (!mounted || authLoading || isLoading) {
    return <FullScreenLoader />
  }

  return (
    <ShaderGenerationScope nodes={nodes}>
    <StudioContext.Provider value={studio}>
    <MotionConfig reducedMotion="user">
    <DarkModeProvider>
      <CanvasViewportContext.Provider value={{ zoom: viewport.zoom }}>
      {/* Global drag preview - single instance for entire canvas */}
      <CustomDragPreview />

      <div className="flex h-full flex-col">
      <StudioSessionTabs panelControls={<div className={chromeHidden ? 'invisible' : undefined}><WorkspacePanelControls panels={[
        { label: `Tool sidebar (${shortcutLabel('panel.sidebar', platform)})`, icon: toolSidebar.side === 'left' ? 'panel-left' : 'panel-right', open: toolSidebar.open, onClick: toolSidebar.toggle },
        { label: `File Manager (${shortcutLabel('panel.files', platform)})`, icon: 'folder', open: !sidebarCollapsed, onClick: handleToggleSidebar },
        { label: `Library (${shortcutLabel('panel.library', platform)})`, icon: 'library', open: libraryOpen, onClick: handleLibraryOpen },
        { label: `Activity (${shortcutLabel('panel.activity', platform)})`, icon: 'activity', open: activityPanelOpen, onClick: () => setActivityPanelOpen(v => !v), status: hasActiveJobs ? 'processing' : activities.some(a => a.status === 'error') ? 'error' : undefined },
        { label: `Tools (${shortcutLabel('panel.tools', platform)})`, icon: 'sliders-horizontal', open: nodeControlsOpen, onClick: () => { setNodeControlsOpen(v => !v); setNodeSettingsCollapsed(false) } },
      ]} /></div>} canvasId={canvasId} title={currentCanvas?.title || 'Session'} actions={canvasActions} saveState={isBoardMode ? { isSaving: studio.busy, lastSaved: null, error: studio.error, pendingChanges: !!studio.error } : saveState} busy={Boolean(saveState.isSaving || saveState.pendingChanges || saveState.error || studio.busy)} />
      <motion.div
        className={`canvas-page min-h-0 flex-1 w-full relative overflow-hidden ${!sidebarCollapsed ? 'sidebar-expanded' : ''}`}
        variants={staggerContainer(0.04)}
        initial="hidden"
        animate="visible"
      >
      {/* Debug: Performance Monitor
      <RenderCounter saveState={saveState} />
      */}


      <div ref={setToolPaletteHost} className={`absolute top-2.5 bottom-11 z-20 pointer-events-none${chromeHidden ? ' invisible' : ''}`} style={{ [toolSidebar.side]: 10 }} />
      <WorkspaceDock className={`bottom-11${chromeHidden ? ' invisible' : ''}`} leftInset={10 + (toolSidebar.side === 'left' ? paletteWidth + 10 : 0)} rightInset={10 + (toolSidebar.side === 'right' ? paletteWidth + 10 : 0)} onSidesChange={setDockSides}>
        <WorkspaceDockPanel id="files" title="File Manager" visible={!sidebarCollapsed} className={filesExpanded ? 'flex-1' : 'shrink-0'}>
        {/* Files Panel — takes available space, scrolls internally */}
        <FilesPanel onClose={() => setSidebarCollapsed(true)} onOpenChange={setFilesExpanded}
          className="pointer-events-auto"
          folders={folders}
          foldersLoading={foldersLoading}
          onCreateFolder={createFolder}
          onRenameFolder={renameFolder}
          onDeleteFolder={deleteFolder}
          onDuplicateFolder={duplicateFolder}
          files={files}
          filesLoading={filesLoading}
          onMoveFileToFolder={moveFile}
          onUploadFile={handleFileUpload}
          onDeleteFile={deleteFile}
        />

        </WorkspaceDockPanel>
        <WorkspaceDockPanel id="node-controls" title="Node controls" visible={nodeControlsOpen} className="flex-1">
          {isBoardMode && <WorkspacePanel title="Node controls" onClose={() => setNodeControlsOpen(false)}><p className="p-4 text-sm text-muted-foreground">Select a workflow node to see its controls.</p></WorkspacePanel>}
          {!isBoardMode && <NodeSettingsPanel
            ref={nodeSettingsPanelRef}
            nodeId={isBoardMode ? undefined : selectedNodeData?.id}
            nodeType={selectedNodeData?.type}
            componentType={selectedNodeData?.componentType}
            nodeTitle={selectedNodeData?.title}
            purpose={selectedNodeData?.purpose}
            subPurpose={selectedNodeData?.subPurpose}
            initialParams={selectedNodeData?.initialParams}
            onCollapse={handleNodeSettingsCollapse}
            collapsed={nodeSettingsCollapsed}
            onClose={() => setNodeControlsOpen(false)}
            onStateChange={selectedNodeStateChange}
            nodeHandles={selectedNodeHandles}
            nodeState={selectedNodeRawState}
            graphNodes={nodes}
            graphEdges={edges}
          />}
        </WorkspaceDockPanel>
        <WorkspaceDockPanel id="activity" title="Activity" visible={activityPanelOpen} className="shrink-0">
          <ActivityPanel onClose={() => setActivityPanelOpen(false)}
            activities={activities}
            defaultOpen={hasActiveJobs}
            onActivityClick={handleActivityClick}
            onGoToNode={handleGoToNode}
            historyTransactions={historyTransactions}
            historyLoading={historyLoading}
            historyError={historyError}
            historyHasMore={historyHasMore}
            onLoadMoreHistory={loadMoreHistory}
          />
        </WorkspaceDockPanel>
        <WorkspaceDockPanel id="library" title="Library" columns={2} visible={libraryOpen} className="flex-1">
          <div ref={setLibraryHost} className="min-h-0 flex-1 flex flex-col" />
          <LibraryPanel open={libraryOpen} host={libraryHost} onOpenChange={setLibraryOpen} onAddToCanvas={handleAddModelToCanvas} onAddShaderToCanvas={handleAddShaderToCanvas} onDragStart={() => studio.setMode('flow')} initialTab={libraryTab} />
        </WorkspaceDockPanel>
      </WorkspaceDock>

      {/* Container breadcrumb — shown when inside a Gallery Container */}
      {!isBoardMode && currentContainerId && (
        <div className="absolute top-4 left-4 z-20 pointer-events-auto">
          <div className="flex items-center gap-2 bg-popover border border-border rounded-lg px-3 py-1.5 shadow-float">
            <button
              onClick={goToRoot}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Canvas
            </button>
            {containerStack.map((cId, i) => {
              const cNode = nodes.find((n) => n.id === cId)
              const cState = (cNode?.data as Record<string, unknown>)?.state as Record<string, unknown> | undefined
              const cName = (cState?.seriesName as string) || (cState?.title as string) || 'Container'
              const isLast = i === containerStack.length - 1
              return (
                <React.Fragment key={cId}>
                  <Icon name="chevron-right" className="h-3 w-3 text-muted-foreground" />
                  {isLast ? (
                    <span className="text-xs font-medium text-foreground">{cName}</span>
                  ) : (
                    <button
                      onClick={() => {
                        // Navigate to this level by popping until we reach it
                        const targetIdx = containerStack.indexOf(cId)
                        const store = useContainerNavigationStore.getState()
                        for (let j = containerStack.length - 1; j > targetIdx; j--) {
                          store.goBack()
                        }
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {cName}
                    </button>
                  )}
                </React.Fragment>
              )
            })}
            <button
              onClick={goBack}
              className="ml-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors bg-muted px-2 py-0.5 rounded"
            >
              <Icon name="arrow-left" className="h-3 w-3" />
              Back
            </button>
          </div>
        </div>
      )}

      {/* Canvas toolbar floats above the footer, centered in the available canvas. */}
      <motion.div
        variants={staggerItem}
        className={`absolute bottom-11 left-0 right-0 z-10 flex justify-center pointer-events-none${chromeHidden ? ' invisible' : ''}`}
        style={{ left: 10 + (toolSidebar.side === 'left' ? paletteWidth + 10 : 0) + (dockSides.left ? dockSides.leftWidth + 10 : 0), right: 10 + (toolSidebar.side === 'right' ? paletteWidth + 10 : 0) + (dockSides.right ? dockSides.rightWidth + 10 : 0) }}
      >
        <div className="max-w-full pointer-events-auto">
          <CanvasToolbar
            paletteHost={toolPaletteHost}
            browseState={toolSidebar.browse.node}
            onBrowseStateChange={value => toolSidebar.setBrowse('node', value)}
            paletteOpen={toolSidebar.open}
            paletteSide={toolSidebar.side}
            onPaletteOpenChange={toolSidebar.setOpen}
            onPaletteSideChange={toolSidebar.setSide}
            onPaletteWidthChange={setPaletteWidth}
            navigation={{ panMode: activeTool === 'hand', onSelect: () => setActiveTool(undefined), onPan: () => setActiveTool('hand'), onFit: () => isBoardMode ? boardZoomApi.current?.fit() : canvasRef.current?.fitView({ padding: 0.2 }) }}
            activeTool={activeTool}
            activeMode={activeMode}
            onToolSelect={tool => { studio.setMode('flow'); handleToolSelect(tool) }}
            onToolDragStart={() => studio.setMode('flow')}
            modeControl={studioEnabled ? <StudioModeControl onAdd={addFrameConnection} /> : undefined}
            onModeChange={setActiveMode}
          />
        </div>
      </motion.div>

      {/* Save status lives in the session tab. */}


      {(() => {
        if (!shaderCodeModalNodeId) return <ShaderCodeModal open={false} nodeId={null} initialCode="" runtimeMode="glsl" title="" onSave={() => {}} onClose={() => setShaderCodeModalNodeId(null)} />
        const targetNode = nodes.find((n) => n.id === shaderCodeModalNodeId)
        const state = ((targetNode?.data as Record<string, unknown> | undefined)?.state ?? {}) as Record<string, unknown>
        return (
          <ShaderCodeModal
            open
            nodeId={shaderCodeModalNodeId}
            initialCode={(state.code as string) ?? ''}
            runtimeMode={(state.runtimeMode as 'glsl' | 'p5' | 'threejs' | 'raw') ?? 'glsl'}
            title={(state.title as string) ?? 'Shader'}
            onSave={(id, code) => handleNodeStateChange(id)((prev) => ({ ...prev, code }))}
            onClose={() => setShaderCodeModalNodeId(null)}
          />
        )
      })()}

      {/* Train Model Dialog */}
      <CreateModelDialog open={trainDialogOpen} onOpenChange={setTrainDialogOpen} />

      {/* Canvas Content Area */}
      <div className="absolute inset-0" style={{ visibility: isBoardMode ? 'hidden' : 'visible' }} inert={isBoardMode} aria-hidden={isBoardMode}>
      <CanvasDropZone
        canvasRef={canvasRef}
        canvasId={canvasId}
        nodes={visibleNodes}
        edges={edges}
        viewport={viewport}
        canvasSize={canvasSize}
        theme={theme}
        isPanning={isPanning}
        isSpacePressed={isSpacePressed || activeTool === 'hand'}
        placementCursor={activeTool === 'comment' ? 'crosshair' : undefined}
        onViewportChange={handleViewportChange}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onSelectionChange={handleSelectionChange}
        onPaneClick={handleCanvasPaneClick}
        onPaneContextMenu={contextMenu.onPaneContextMenu}
        onNodeContextMenu={contextMenu.onNodeContextMenu}
        onEdgeContextMenu={contextMenu.onEdgeContextMenu}
        onConnectionDropped={connectionDropMenu.handleConnectionDropped}
        onToolDrop={(tool, screenPos) => { studio.setMode('flow'); handleToolDrop(tool, screenPos) }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onNodeDragStop={handleNodeDragStop}
        config={CANVAS_DROP_ZONE_CONFIG}
        showControls={showControls}
        showMiniMap={showMiniMap}
        setNodes={setNodes}
        setEdges={setEdges}
      />
      </div>
      {isBoardMode && <div className="absolute inset-0" onDragOver={event => event.preventDefault()} onDrop={event => {
        event.preventDefault()
        void (async () => { for (const file of Array.from(event.dataTransfer.files)) await studio.importImage(file) })().catch(() => {})
      }}><StudioBoardCanvas tool={activeTool === 'hand' ? 'hand' : 'select'} onZoomApi={onBoardZoomApi} onZoom={setBoardZoom} /></div>}


      {/* Multi-selection action bar */}
      {!isBoardMode && <MultiSelectionBar
        selectedCount={selectedNodes.length}
        onAlign={alignNodes}
        onDistribute={distributeNodes}
        onDuplicate={shortcutActions.duplicateSelected}
        onDelete={shortcutActions.deleteSelected}
      />}

      {/* Status bar */}
      <div className={chromeHidden ? 'invisible' : undefined}>
      <StatusBar
        credits={<CanvasCreditsPanel />}
        zoom={isBoardMode ? boardZoom : viewport.zoom}
        nodeCount={isBoardMode ? studio.project?.artboards.length ?? 0 : nodes.length}
        itemLabel={isBoardMode ? 'frame' : 'node'}
        selectedCount={isBoardMode ? Number(Boolean(studio.selectedFrameId)) : selectedNodes.length}
        saveState={isBoardMode ? { isSaving: studio.busy, lastSaved: null, error: studio.error, pendingChanges: !!studio.error } : saveState}
        onZoomIn={() => isBoardMode ? boardZoomApi.current?.zoomIn() : canvasRef.current?.zoomIn()}
        onZoomOut={() => isBoardMode ? boardZoomApi.current?.zoomOut() : canvasRef.current?.zoomOut()}
        onZoomReset={() => isBoardMode ? boardZoomApi.current?.reset() : canvasRef.current?.zoomTo(1)}
        onShowShortcuts={() => setShowShortcuts(prev => !prev)}
      />
      </div>
      {chromeHidden && <p className="sr-only" role="status">Interface hidden. Press {shortcutLabel('chrome.toggle-ui', platform)} to show toolbars and panels again.</p>}

      {/* Context Menu (right-click) */}
      <CanvasContextMenu
        menu={contextMenu.menu}
        onClose={contextMenu.close}
        nodes={nodes}
        edges={edges}
        hasClipboard={false}
        actions={contextMenuActions}
      />

      {/* Command Palette (Cmd+K) */}
      <CommandPalette open={showCommandPalette} onOpenChange={setShowCommandPalette} context={commandContext} />

      {/* Keyboard Shortcuts Overlay */}
      <KeyboardShortcutsOverlay isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} mode={workspaceMode} />
      {workspaceSearchOpen && <WorkspaceCommandDialog mode={workspaceMode} host={WEB_HOST} platform={platform} selectionCount={selectedNodes.length} searching onRun={(command) => {
        const current: ChromeState = {
          mode: workspaceMode,
          visibility: { sidebar: toolSidebar.open, files: !sidebarCollapsed, library: libraryOpen, activity: activityPanelOpen, tools: nodeControlsOpen, genie: false, apps: false },
          layout: 'genie.workspace.panel-layout',
          selection: selectedNodes,
          chromeHidden,
          editorOpen: false,
        }
        const next = applyWorkspaceCommand(current, command)
        if (next.effect.type === 'chrome') setChromeHidden(next.state.chromeHidden)
        if (next.effect.type === 'mode' && next.effect.mode !== 'chat') studio.setMode(next.effect.mode)
        if (next.effect.type === 'panel') applyPanel(next.effect.panel, next.effect.open)
        if (next.effect.type === 'help') setShowShortcuts(true)
        if (next.effect.type === 'search' && next.effect.which === 'tools') { setLibraryOpen(true); toolSidebar.setOpen(true) }
      }} onClose={() => setWorkspaceSearchOpen(false)} />}

      {/* Modals */}
      <CanvasModals
        showRenameDialog={showRenameDialog}
        setShowRenameDialog={setShowRenameDialog}
        newCanvasName={newCanvasName}
        setNewCanvasName={setNewCanvasName}
        onRenameConfirm={handleRename}
        showDeleteDialog={showDeleteDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        canvasTitle={currentCanvas?.title || ''}
        onDeleteConfirm={handleDeleteConfirm}
        showDuplicateDialog={showDuplicateDialog}
        setShowDuplicateDialog={setShowDuplicateDialog}
        onDuplicateConfirm={handleDuplicateConfirm}
        showNewCanvasDialog={showNewCanvasDialog}
        setShowNewCanvasDialog={setShowNewCanvasDialog}
        onNewCanvasConfirm={handleNewCanvasConfirm}
      />

      {/* Connection Drop Context Menu */}
      <ConnectionDropMenu
        menuState={connectionDropMenu.menuState}
        availableNodeTypes={connectionDropMenu.getAvailableNodeTypes()}
        onNodeTypeSelect={connectionDropMenu.handleNodeTypeSelect}
        onClose={connectionDropMenu.closeMenu}
      />

      {/* Insufficient Balance Dialog (global, triggered by Zustand store) */}
      <InsufficientBalanceDialog />
    </motion.div>
    </div>
    </CanvasViewportContext.Provider>
    </DarkModeProvider>
    </MotionConfig>
    </StudioContext.Provider>
    </ShaderGenerationScope>
  )
}
